import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  CategoryOutlined,
  CurrencyExchangeOutlined,
  FileDownloadOutlined,
  Inventory2Outlined,
  PictureAsPdfOutlined,
  RefreshOutlined,
  WarningAmberOutlined,
} from "@mui/icons-material";
import {
  Document as PdfDocument,
  Image as PdfImage,
  Page as PdfPage,
  StyleSheet,
  Text as PdfText,
  View as PdfView,
  pdf,
} from "@react-pdf/renderer";
import * as XLSX from "xlsx";

import fallbackLogo from "../../assets/pawsi.jpeg";
import CustomSnackBar from "../../Components/CustomSnackBar";
import MainText from "../../Components/MainText";
import { hasPermission } from "../../config/permissionUtils";
import { useAuth } from "../../context/AuthContext";
import { inventoryService } from "../../services/modules/inventory.service";
import ActiveGeneralInventoryAlert from "./components/ActiveGeneralInventoryAlert";
import { buildStockReportHtml } from "./stockReportTemplate";

const LOW_STOCK_THRESHOLD = 5;

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.results)) return value.results;
  return [];
};

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

function useDebouncedValue(value, delay = 300) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timeout = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timeout);
  }, [delay, value]);

  return debounced;
}

const formatMoney = (value) => `Q${toNumber(value).toFixed(2)}`;

const reportDateTime = () =>
  new Date().toLocaleString("es-GT", {
    dateStyle: "medium",
    timeStyle: "short",
  });

const todayFileDate = () => new Date().toISOString().slice(0, 10);

const sanitizeFilePart = (value) =>
  String(value || "Sucursal")
    .trim()
    .replace(/[\\/:*?"<>|]+/g, "")
    .replace(/\s+/g, "_")
    .slice(0, 60) || "Sucursal";

const formatCompactQty = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return "0";
  if (Number.isInteger(parsed)) return String(parsed);
  return parsed.toFixed(3).replace(/\.?0+$/, "");
};

const formatHumanUnits = (qtyBase, qtyInBase, presentationName) => {
  const safeQtyBase = toNumber(qtyBase);
  const safeQtyInBase = toNumber(qtyInBase);
  if (safeQtyInBase <= 0) return `0 ${presentationName || "unidades"}`;
  const units = safeQtyBase / safeQtyInBase;
  return `${formatCompactQty(units)} ${presentationName || "unidades"}`;
};

const formatReadableStock = ({
  qtyBase,
  maxQtyInBase,
  maxName,
  minQtyInBase,
  minName,
}) => {
  const safeQtyBase = toNumber(qtyBase);
  const safeMaxQtyInBase = toNumber(maxQtyInBase);
  const safeMinQtyInBase = toNumber(minQtyInBase);

  if (safeQtyBase <= 0) {
    return `0 ${minName || maxName || "unidades"}`;
  }

  if (safeMaxQtyInBase <= 0 || safeMinQtyInBase <= 0) {
    return formatHumanUnits(safeQtyBase, safeMinQtyInBase || safeMaxQtyInBase || 1, minName || maxName);
  }

  if (safeMaxQtyInBase <= safeMinQtyInBase) {
    return formatHumanUnits(safeQtyBase, safeMinQtyInBase, minName || maxName);
  }

  const maxUnits = Math.floor(safeQtyBase / safeMaxQtyInBase);
  const remainderBase = safeQtyBase - maxUnits * safeMaxQtyInBase;
  const minUnits = remainderBase / safeMinQtyInBase;
  const parts = [];

  if (maxUnits > 0) {
    parts.push(`${formatCompactQty(maxUnits)} ${maxName || "unidades"}`);
  }
  if (minUnits > 0) {
    parts.push(`${formatCompactQty(minUnits)} ${minName || "unidades"}`);
  }

  if (parts.length === 0) {
    return `0 ${minName || maxName || "unidades"}`;
  }
  return parts.join(" y ");
};

const stockStatusOf = (qtyBase) => {
  const safeQty = toNumber(qtyBase);
  if (safeQty <= 0) {
    return { key: "OUT", label: "Agotado", color: "error" };
  }
  if (safeQty <= LOW_STOCK_THRESHOLD) {
    return { key: "LOW", label: "Stock bajo", color: "warning" };
  }
  return { key: "OK", label: "Disponible", color: "success" };
};

const getBranchName = (branch) =>
  branch?.alias || branch?.name || branch?.legalName || branch?.business_name || "Sucursal";

const getBranchAddress = (branch) =>
  branch?.address || branch?.tax_address || branch?.branch_tax_address || "No disponible";

const getUserName = (user) => {
  const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(" ").trim();
  return fullName || user?.name || user?.username || user?.email || "Usuario";
};

function KpiCard({ icon, label, value, color = "primary.main" }) {
  return (
    <Card variant="outlined">
      <CardContent sx={{ py: 1.4, "&:last-child": { pb: 1.4 } }}>
        <Stack direction="row" spacing={1.2} alignItems="center">
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              display: "grid",
              placeItems: "center",
              bgcolor: "action.hover",
              color,
            }}
          >
            {icon}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
              {value}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {label}
            </Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

const pdfStyles = StyleSheet.create({
  page: {
    padding: 28,
    fontSize: 8,
    fontFamily: "Helvetica",
    color: "#1f2933",
  },
  header: {
    borderBottomWidth: 3,
    borderBottomColor: "#757B2B",
    paddingBottom: 8,
    marginBottom: 10,
    flexDirection: "row",
    justifyContent: "space-between",
    gap: 12,
  },
  brand: {
    flexDirection: "row",
    gap: 8,
    flexGrow: 1,
  },
  logo: {
    width: 42,
    height: 42,
    objectFit: "contain",
  },
  title: {
    fontSize: 16,
    fontWeight: 700,
    color: "#757B2B",
    textTransform: "uppercase",
  },
  muted: {
    color: "#6b7280",
  },
  meta: {
    textAlign: "right",
    lineHeight: 1.4,
  },
  infoGrid: {
    borderWidth: 1,
    borderColor: "#e5e7eb",
    backgroundColor: "#f8fafc",
    padding: 8,
    marginBottom: 9,
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 4,
  },
  infoCell: {
    width: "48%",
  },
  label: {
    fontWeight: 700,
  },
  kpis: {
    flexDirection: "row",
    gap: 6,
    marginBottom: 9,
  },
  kpi: {
    flexGrow: 1,
    borderWidth: 1,
    borderColor: "#e5e7eb",
    borderTopWidth: 2,
    borderTopColor: "#F3694D",
    padding: 6,
  },
  kpiValue: {
    fontSize: 12,
    fontWeight: 700,
    color: "#757B2B",
  },
  table: {
    borderWidth: 1,
    borderColor: "#e5e7eb",
  },
  row: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#e5e7eb",
  },
  tableHeader: {
    backgroundColor: "#757B2B",
    color: "#ffffff",
    fontWeight: 700,
  },
  cell: {
    padding: 4,
  },
  codeCell: { width: "11%" },
  productCell: { width: "25%" },
  categoryCell: { width: "14%" },
  presentationCell: { width: "13%" },
  stockCell: { width: "16%" },
  statusCell: { width: "9%" },
  moneyCell: { width: "12%", textAlign: "right" },
  totalBox: {
    marginTop: 10,
    marginLeft: "auto",
    width: 180,
    borderWidth: 1.5,
    borderColor: "#757B2B",
    padding: 8,
    backgroundColor: "#f7f8ef",
  },
  totalValue: {
    fontSize: 14,
    fontWeight: 700,
    color: "#757B2B",
    textAlign: "right",
  },
  footer: {
    marginTop: 10,
    paddingTop: 6,
    borderTopWidth: 1,
    borderTopColor: "#e5e7eb",
    color: "#6b7280",
    flexDirection: "row",
    justifyContent: "space-between",
  },
});

function StockReportPdfDocument({ report }) {
  const rows = Array.isArray(report?.rows) ? report.rows : [];
  const summary = report?.summary || {};
  const filters = report?.filters || {};

  return (
    <PdfDocument>
      <PdfPage size="LETTER" style={pdfStyles.page} wrap>
        <PdfView style={pdfStyles.header} fixed>
          <PdfView style={pdfStyles.brand}>
            {report?.logoUrl ? <PdfImage src={report.logoUrl} style={pdfStyles.logo} /> : null}
            <PdfView>
              <PdfText style={pdfStyles.title}>Reporte de Existencias</PdfText>
              <PdfText style={pdfStyles.muted}>Control de stock actual por producto</PdfText>
            </PdfView>
          </PdfView>
          <PdfView style={pdfStyles.meta}>
            <PdfText>Fecha: {report?.reportDate || "-"}</PdfText>
            <PdfText>Sucursal: {report?.branchName || "-"}</PdfText>
            <PdfText>Generado por: {report?.userName || "-"}</PdfText>
          </PdfView>
        </PdfView>

        <PdfView style={pdfStyles.infoGrid}>
          <PdfText style={pdfStyles.infoCell}>
            <PdfText style={pdfStyles.label}>Sucursal: </PdfText>
            {report?.branchName || "-"}
          </PdfText>
          <PdfText style={pdfStyles.infoCell}>
            <PdfText style={pdfStyles.label}>Direccion: </PdfText>
            {report?.branchAddress || "-"}
          </PdfText>
          <PdfText style={pdfStyles.infoCell}>
            <PdfText style={pdfStyles.label}>Busqueda: </PdfText>
            {filters.query || "Todos"}
          </PdfText>
          <PdfText style={pdfStyles.infoCell}>
            <PdfText style={pdfStyles.label}>Estado: </PdfText>
            {filters.status || "Todos"}
          </PdfText>
          <PdfText style={pdfStyles.infoCell}>
            <PdfText style={pdfStyles.label}>Categoria: </PdfText>
            {filters.category || "Todas"}
          </PdfText>
          <PdfText style={pdfStyles.infoCell}>
            <PdfText style={pdfStyles.label}>Total registros: </PdfText>
            {rows.length}
          </PdfText>
        </PdfView>

        <PdfView style={pdfStyles.kpis}>
          <PdfView style={pdfStyles.kpi}>
            <PdfText style={pdfStyles.kpiValue}>{summary.productsWithStock}</PdfText>
            <PdfText style={pdfStyles.muted}>Productos con stock</PdfText>
          </PdfView>
          <PdfView style={pdfStyles.kpi}>
            <PdfText style={pdfStyles.kpiValue}>{summary.productsOut}</PdfText>
            <PdfText style={pdfStyles.muted}>Productos agotados</PdfText>
          </PdfView>
          <PdfView style={pdfStyles.kpi}>
            <PdfText style={pdfStyles.kpiValue}>{summary.productsLow}</PdfText>
            <PdfText style={pdfStyles.muted}>Stock bajo</PdfText>
          </PdfView>
          <PdfView style={pdfStyles.kpi}>
            <PdfText style={pdfStyles.kpiValue}>{summary.estimatedValue}</PdfText>
            <PdfText style={pdfStyles.muted}>Valor estimado</PdfText>
          </PdfView>
        </PdfView>

        <PdfView style={pdfStyles.table}>
          <PdfView style={[pdfStyles.row, pdfStyles.tableHeader]} fixed>
            <PdfText style={[pdfStyles.cell, pdfStyles.codeCell]}>Codigo</PdfText>
            <PdfText style={[pdfStyles.cell, pdfStyles.productCell]}>Producto</PdfText>
            <PdfText style={[pdfStyles.cell, pdfStyles.categoryCell]}>Categoria</PdfText>
            <PdfText style={[pdfStyles.cell, pdfStyles.presentationCell]}>Unidad minima/base</PdfText>
            <PdfText style={[pdfStyles.cell, pdfStyles.stockCell]}>Existencia</PdfText>
            <PdfText style={[pdfStyles.cell, pdfStyles.statusCell]}>Estado</PdfText>
            <PdfText style={[pdfStyles.cell, pdfStyles.moneyCell]}>Valorizado</PdfText>
          </PdfView>
          {rows.length === 0 ? (
            <PdfText style={pdfStyles.cell}>Sin datos para los filtros aplicados.</PdfText>
          ) : (
            rows.map((row, index) => (
              <PdfView key={`${row.code}-${index}`} style={pdfStyles.row} wrap={false}>
                <PdfText style={[pdfStyles.cell, pdfStyles.codeCell]}>{row.code}</PdfText>
                <PdfText style={[pdfStyles.cell, pdfStyles.productCell]}>{row.product}</PdfText>
                <PdfText style={[pdfStyles.cell, pdfStyles.categoryCell]}>{row.category}</PdfText>
                <PdfText style={[pdfStyles.cell, pdfStyles.presentationCell]}>{row.presentation}</PdfText>
                <PdfText style={[pdfStyles.cell, pdfStyles.stockCell]}>{row.readableStock}</PdfText>
                <PdfText style={[pdfStyles.cell, pdfStyles.statusCell]}>{row.statusLabel}</PdfText>
                <PdfText style={[pdfStyles.cell, pdfStyles.moneyCell]}>{row.estimatedValue}</PdfText>
              </PdfView>
            ))
          )}
        </PdfView>

        <PdfView style={pdfStyles.totalBox}>
          <PdfText style={pdfStyles.label}>VALORIZADO GENERAL</PdfText>
          <PdfText style={pdfStyles.totalValue}>{summary.estimatedValue}</PdfText>
        </PdfView>

        <PdfView style={pdfStyles.footer} fixed>
          <PdfText>Sistema Punto de Venta</PdfText>
          <PdfText render={({ pageNumber, totalPages }) => `Pagina ${pageNumber} de ${totalPages}`} />
        </PdfView>
      </PdfPage>
    </PdfDocument>
  );
}

function StocksPage() {
  const { currentBranch, user, isSuperuser, effectivePerms = [] } = useAuth();
  const branchId = currentBranch?.id || null;
  const canView = Boolean(isSuperuser || effectivePerms.includes("inventory_stock_view"));
  const canViewReports = hasPermission(effectivePerms, "view_reports", isSuperuser);

  const [query, setQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const reportFrameRef = useRef(null);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportHtml, setReportHtml] = useState("");
  const [reportPayload, setReportPayload] = useState(null);
  const [pdfDownloading, setPdfDownloading] = useState(false);

  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState("");
  const [snackSev, setSnackSev] = useState("info");

  const debouncedQuery = useDebouncedValue(query, 300);

  const showSnack = (message, severity = "info") => {
    setSnackMsg(message);
    setSnackSev(severity);
    setSnackOpen(true);
  };

  const loadRows = useCallback(async () => {
    if (!branchId || !canView) {
      setRows([]);
      return;
    }
    setLoading(true);
    try {
      const data = await inventoryService.listStocks({ branch_id: branchId });
      setRows(toArray(data));
    } catch {
      setRows([]);
      showSnack("No se pudieron cargar las existencias.", "error");
    } finally {
      setLoading(false);
    }
  }, [branchId, canView]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const categoryOptions = useMemo(() => {
    const byId = new Map();
    rows.forEach((row) => {
      const categoryId = row?.category_id;
      const categoryName = row?.category_name;
      if (!categoryId || !categoryName) return;
      if (!byId.has(String(categoryId))) {
        byId.set(String(categoryId), { id: String(categoryId), label: categoryName });
      }
    });
    return Array.from(byId.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [rows]);

  const filteredRows = useMemo(() => {
    const normalizedQuery = debouncedQuery.trim().toLowerCase();
    return rows.filter((row) => {
      if (categoryFilter && String(row?.category_id || "") !== String(categoryFilter)) {
        return false;
      }

      const status = stockStatusOf(row?.qty_base).key;
      if (statusFilter === "AVAILABLE" && status !== "OK") return false;
      if (statusFilter === "LOW" && status !== "LOW") return false;
      if (statusFilter === "OUT" && status !== "OUT") return false;

      if (!normalizedQuery) return true;
      const haystack = [
        row?.product_description,
        row?.product_code,
        row?.presentation_name,
        row?.category_name,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(normalizedQuery);
    });
  }, [categoryFilter, debouncedQuery, rows, statusFilter]);

  const stockSummary = useMemo(() => {
    const byProduct = new Map();
    filteredRows.forEach((row) => {
      const productId = String(row?.product_id || row?.id);
      const current = byProduct.get(productId) || {
        qtyBase: 0,
        hasStock: false,
      };
      const qtyBase = toNumber(row?.qty_base);
      current.qtyBase += qtyBase;
      current.hasStock = current.hasStock || qtyBase > 0;
      byProduct.set(productId, current);
    });

    let productsWithStock = 0;
    let productsOut = 0;
    let productsLow = 0;
    let estimatedValue = 0;

    if (canViewReports) {
      filteredRows.forEach((row) => {
        estimatedValue += toNumber(row?.estimated_value);
      });
    }

    byProduct.forEach((row) => {
      if (row.qtyBase <= 0) {
        productsOut += 1;
        return;
      }
      if (row.qtyBase <= LOW_STOCK_THRESHOLD) {
        productsLow += 1;
      }
      productsWithStock += 1;
    });

    return {
      productsWithStock,
      productsOut,
      productsLow,
      estimatedValue,
    };
  }, [canViewReports, filteredRows]);

  const reportFilters = useMemo(() => {
    const categoryLabel =
      categoryOptions.find((option) => String(option.id) === String(categoryFilter))?.label || "Todas";
    const statusLabels = {
      ALL: "Todos",
      AVAILABLE: "Disponible",
      LOW: "Stock bajo",
      OUT: "Agotado",
    };
    return {
      query: String(query || "").trim() || "Todos",
      category: categoryLabel,
      status: statusLabels[statusFilter] || "Todos",
    };
  }, [categoryFilter, categoryOptions, query, statusFilter]);

  const reportRows = useMemo(
    () =>
      filteredRows.map((row) => {
        const status = stockStatusOf(row?.qty_base);
        const readableStock = formatReadableStock({
          qtyBase: row?.qty_base,
          maxQtyInBase: row?.max_presentation_qty_in_base,
          maxName: row?.max_presentation_name,
          minQtyInBase: row?.min_presentation_qty_in_base,
          minName: row?.min_presentation_name,
        });
        const minEquivalence = formatHumanUnits(
          row?.qty_base,
          row?.min_presentation_qty_in_base,
          row?.min_presentation_name
        );
        return {
          code: row?.product_code || "-",
          product: row?.product_description || "-",
          category: row?.category_name || "-",
          presentation: row?.min_presentation_name || row?.presentation_name || "-",
          readableStock,
          qtyBase: toNumber(row?.qty_base),
          minPresentation: row?.min_presentation_name || "-",
          minEquivalence,
          costValue: row?.cost_price == null ? null : toNumber(row.cost_price),
          estimatedValueRaw: row?.estimated_value == null ? null : toNumber(row.estimated_value),
          cost: row?.cost_price == null ? "-" : formatMoney(row.cost_price),
          estimatedValue: row?.estimated_value == null ? "-" : formatMoney(row.estimated_value),
          statusKey: status.key,
          statusLabel: status.label,
        };
      }),
    [filteredRows]
  );

  const reportMeta = useMemo(
    () => ({
      branchName: getBranchName(currentBranch),
      branchAddress: getBranchAddress(currentBranch),
      userName: getUserName(user),
    }),
    [currentBranch, user]
  );

  const buildReportPayload = useCallback(() => {
    const generatedAt = reportDateTime();
    return {
      logoUrl:
        currentBranch?.logo_url ||
        currentBranch?.logo ||
        currentBranch?.company_logo ||
        currentBranch?.company?.logo_url ||
        fallbackLogo,
      reportDate: generatedAt,
      branchName: reportMeta.branchName,
      branchAddress: reportMeta.branchAddress,
      userName: reportMeta.userName,
      filters: reportFilters,
      summary: {
        productsWithStock: stockSummary.productsWithStock,
        productsOut: stockSummary.productsOut,
        productsLow: stockSummary.productsLow,
        estimatedValue: formatMoney(stockSummary.estimatedValue),
      },
      rows: reportRows,
    };
  }, [currentBranch, reportFilters, reportMeta, reportRows, stockSummary]);

  const handleExportExcel = () => {
    if (!canViewReports) return;
    const generatedAt = reportDateTime();
    const sheetRows = [
      ["REPORTE DE EXISTENCIAS"],
      ["Empresa/Sucursal", reportMeta.branchName],
      ["Direccion", reportMeta.branchAddress],
      ["Fecha de generacion", generatedAt],
      ["Filtros", `Busqueda: ${reportFilters.query}; Categoria: ${reportFilters.category}; Estado: ${reportFilters.status}`],
      [],
      [
        "Producto",
        "Codigo",
        "Categoria",
        "Unidad minima/base",
        "Existencia legible",
        "Existencia base",
        "Presentacion minima",
        "Equivalencia minima",
        "Costo unitario",
        "Valor estimado",
        "Estado",
      ],
      ...reportRows.map((row) => [
        row.product,
        row.code,
        row.category,
        row.presentation,
        row.readableStock,
        row.qtyBase,
        row.minPresentation,
        row.minEquivalence,
        row.costValue,
        row.estimatedValueRaw,
        row.statusLabel,
      ]),
      [],
      ["Resumen"],
      ["Total valorizado general", stockSummary.estimatedValue],
      ["Productos con stock", stockSummary.productsWithStock],
      ["Agotados", stockSummary.productsOut],
      ["Stock bajo", stockSummary.productsLow],
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(sheetRows);
    worksheet["!cols"] = [
      { wch: 34 },
      { wch: 16 },
      { wch: 20 },
      { wch: 18 },
      { wch: 26 },
      { wch: 15 },
      { wch: 20 },
      { wch: 22 },
      { wch: 16 },
      { wch: 16 },
      { wch: 14 },
    ];
    worksheet["!freeze"] = { xSplit: 0, ySplit: 7 };

    const headerRowIndex = 6;
    for (let columnIndex = 0; columnIndex <= 10; columnIndex += 1) {
      const cellRef = XLSX.utils.encode_cell({ r: headerRowIndex, c: columnIndex });
      if (worksheet[cellRef]) {
        worksheet[cellRef].s = { font: { bold: true } };
      }
    }

    const firstDataRow = 7;
    const lastDataRow = firstDataRow + reportRows.length - 1;
    for (let rowIndex = firstDataRow; rowIndex <= lastDataRow; rowIndex += 1) {
      [8, 9].forEach((columnIndex) => {
        const cellRef = XLSX.utils.encode_cell({ r: rowIndex, c: columnIndex });
        if (worksheet[cellRef] && worksheet[cellRef].v != null) {
          worksheet[cellRef].t = "n";
          worksheet[cellRef].z = '"Q" #,##0.00';
        }
      });
    }
    const summaryTotalRef = XLSX.utils.encode_cell({ r: firstDataRow + reportRows.length + 2, c: 1 });
    if (worksheet[summaryTotalRef]) {
      worksheet[summaryTotalRef].t = "n";
      worksheet[summaryTotalRef].z = '"Q" #,##0.00';
    }

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Existencias");
    const filename = `Reporte_Existencias_${sanitizeFilePart(reportMeta.branchName)}_${todayFileDate()}.xlsx`;
    XLSX.writeFile(workbook, filename);
    showSnack("Reporte Excel generado.", "success");
  };

  const handleGeneratePdf = () => {
    if (!canViewReports) return;
    const payload = buildReportPayload();
    setReportPayload(payload);
    setReportHtml(buildStockReportHtml(payload));
    setReportDialogOpen(true);
  };

  const handlePrintReport = () => {
    const frameWindow = reportFrameRef.current?.contentWindow;
    if (!frameWindow) {
      showSnack("No se pudo preparar la impresion del reporte.", "error");
      return;
    }
    frameWindow.focus();
    frameWindow.print();
  };

  const handleDownloadPdf = async () => {
    if (!reportPayload) return;
    setPdfDownloading(true);
    try {
      let blob;
      try {
        blob = await pdf(<StockReportPdfDocument report={reportPayload} />).toBlob();
      } catch (error) {
        blob = await pdf(
          <StockReportPdfDocument report={{ ...reportPayload, logoUrl: "" }} />
        ).toBlob();
      }
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Reporte_Existencias_${sanitizeFilePart(reportMeta.branchName)}_${todayFileDate()}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      showSnack("Reporte PDF descargado.", "success");
    } catch {
      showSnack("No se pudo descargar el PDF.", "error");
    } finally {
      setPdfDownloading(false);
    }
  };

  const columns = useMemo(
    () => [
{
  field: "product_description",
  headerName: "Producto",
  minWidth: 260,
  flex: 1.3,
  renderCell: ({ row }) => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        minWidth: 0,
        width: "100%",
        lineHeight: 1.2,
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
        {row?.product_description || "-"}
      </Typography>
      <Typography variant="caption" color="text.secondary" noWrap>
        {row?.product_code || "Sin código"}
      </Typography>
    </Box>
  ),
},
      {
        field: "category_name",
        headerName: "Categoria",
        minWidth: 180,
        flex: 0.8,
        valueGetter: (_, row) => row?.category_name || "-",
      },
      {
        field: "presentation_name",
        headerName: "Unidad minima/base",
        minWidth: 180,
        flex: 0.8,
        valueGetter: (_, row) => row?.min_presentation_name || row?.presentation_name || "-",
      },
      {
        field: "readable_stock",
        headerName: "Existencia legible",
        minWidth: 240,
        flex: 1,
        valueGetter: (_, row) =>
          formatReadableStock({
            qtyBase: row?.qty_base,
            maxQtyInBase: row?.max_presentation_qty_in_base,
            maxName: row?.max_presentation_name,
            minQtyInBase: row?.min_presentation_qty_in_base,
            minName: row?.min_presentation_name,
          }),
      },
      {
        field: "min_equivalence",
        headerName: "Equivalencia minima",
        minWidth: 190,
        flex: 0.9,
        valueGetter: (_, row) =>
          formatHumanUnits(
            row?.qty_base,
            row?.min_presentation_qty_in_base,
            row?.min_presentation_name
          ),
      },
      {
        field: "status",
        headerName: "Estado",
        minWidth: 160,
        flex: 0.7,
        sortable: false,
        renderCell: ({ row }) => {
          const status = stockStatusOf(row?.qty_base);
          return <Chip size="small" color={status.color} label={status.label} />;
        },
      },
    ],
    []
  );

  if (!canView) {
    return (
      <Box sx={{ py: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Sin permiso
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No tienes permisos para ver existencias.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1.2}>
        <MainText title="Existencias" subtitle="Control de stock actual por producto" icon={Inventory2Outlined} />
        <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
          <TextField
            size="small"
            label="Buscar"
            placeholder="Producto o codigo"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            sx={{ minWidth: 220 }}
          />
          <TextField
            select
            size="small"
            label="Categoria"
            value={categoryFilter}
            onChange={(event) => setCategoryFilter(event.target.value)}
            sx={{ minWidth: 190 }}
          >
            <MenuItem value="">Todas</MenuItem>
            {categoryOptions.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Estado"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            sx={{ minWidth: 170 }}
          >
            <MenuItem value="ALL">Todos</MenuItem>
            <MenuItem value="AVAILABLE">Disponible</MenuItem>
            <MenuItem value="LOW">Stock bajo</MenuItem>
            <MenuItem value="OUT">Agotado</MenuItem>
          </TextField>
          <Button variant="outlined" startIcon={<RefreshOutlined />} onClick={loadRows}>
            Refrescar
          </Button>
          {canViewReports ? (
            <>
              <Button
                variant="outlined"
                startIcon={<FileDownloadOutlined />}
                onClick={handleExportExcel}
                disabled={loading}
              >
                Exportar Excel
              </Button>
              <Button
                variant="outlined"
                startIcon={<PictureAsPdfOutlined />}
                onClick={handleGeneratePdf}
                disabled={loading}
              >
                Generar PDF
              </Button>
            </>
          ) : null}
        </Stack>
      </Stack>

      <ActiveGeneralInventoryAlert />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
            xl: canViewReports ? "repeat(4, minmax(0, 1fr))" : "repeat(3, minmax(0, 1fr))",
          },
          gap: 1.2,
        }}
      >
        <KpiCard
          icon={<Inventory2Outlined fontSize="small" />}
          label="Productos con stock"
          value={stockSummary.productsWithStock}
          color="primary.main"
        />
        <KpiCard
          icon={<WarningAmberOutlined fontSize="small" />}
          label="Productos agotados"
          value={stockSummary.productsOut}
          color="error.main"
        />
        <KpiCard
          icon={<CategoryOutlined fontSize="small" />}
          label="Stock bajo"
          value={stockSummary.productsLow}
          color="warning.main"
        />
        {canViewReports ? (
          <KpiCard
            icon={<CurrencyExchangeOutlined fontSize="small" />}
            label="Valor estimado"
            value={formatMoney(stockSummary.estimatedValue)}
            color="success.main"
          />
        ) : null}
      </Box>

      <Paper variant="outlined" sx={{ height: "calc(100vh - 340px)", minHeight: 420 }}>
        <DataGrid
          rows={filteredRows}
          columns={columns}
          rowHeight={55}
          loading={loading}
          disableRowSelectionOnClick
          getRowId={(row) => row.id}
          pageSizeOptions={[10, 20, 50]}
          initialState={{
            pagination: { paginationModel: { pageSize: 10, page: 0 } },
            sorting: { sortModel: [{ field: "product_description", sort: "asc" }] },
          }}
                sx={{
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#757B2B",
              color: "#ffffff",
            },
            "& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:hover":
              {
                backgroundColor: "#757B2B !important",
              },
            "& .MuiDataGrid-columnHeaderTitle, & .MuiDataGrid-iconButtonContainer, & .MuiDataGrid-menuIcon, & .MuiDataGrid-columnSeparator":
              {
                color: "#ffffff !important",
              },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "rgba(16, 23, 60, 0.08)",
            },
          }}

        />
      </Paper>

      <Dialog
        open={reportDialogOpen}
        onClose={() => setReportDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Reporte de existencias</DialogTitle>
        <DialogContent dividers sx={{ p: 0, bgcolor: "grey.100" }}>
          <Box
            component="iframe"
            ref={reportFrameRef}
            title="Preview reporte de existencias"
            srcDoc={reportHtml}
            sx={{
              display: "block",
              width: "100%",
              height: { xs: "70vh", md: "76vh" },
              border: 0,
              bgcolor: "common.white",
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDownloadPdf} disabled={pdfDownloading || !reportPayload}>
            {pdfDownloading ? "Descargando..." : "Descargar PDF"}
          </Button>
          <Button variant="contained" onClick={handlePrintReport} disabled={!reportHtml}>
            Imprimir
          </Button>
          <Button onClick={() => setReportDialogOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <CustomSnackBar
        open={snackOpen}
        setOpen={setSnackOpen}
        message={snackMsg}
        severity={snackSev}
      />
    </Box>
  );
}

export default StocksPage;
